import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import { mdiCalendarClockOutline, mdiHistory } from "@mdi/js"
import { buildAppointmentTimeTagPresentation } from "../../../../../_shared/_logic/appointment-time-tags"
import { buildScheduleEventCommunicationTagPresentation } from "../../../../../_shared/_logic/communication-presentation"
import { displayScheduleStatusLabel } from "../../../../../_shared/_logic/status-definitions"
import {
  buildRichTooltip,
  tooltipField,
  tooltipText,
  tooltipTitle,
} from "../../../../../_shared/_logic/rich-tooltips"

export type WorkOrderAppointmentHistoryItem = {
  id: string
  label: string
  subLabel: string | null
  status: StatusDefinition<string>
  tooltip: string
}

const normalizeStatus = (value: unknown) => `${value ?? ""}`.trim().toUpperCase()

const parseDate = (value?: string | null) => {
  if (!value) return 0

  const time = new Date(value).getTime()

  return Number.isFinite(time) ? time : 0
}

const parseDateValue = (value?: string | null) => {
  if (!value) return null

  const parsed = new Date(value)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const eventStartTime = (event: ScheduleEvent) =>
  parseDate(
    (event as { start_at?: string | null }).start_at ??
      (event as { timeframe_start_at?: string | null }).timeframe_start_at,
  )

const eventCreatedTime = (event: ScheduleEvent) =>
  parseDate((event as { created_at?: string | null }).created_at) || eventStartTime(event)

const eventLabel = (event: ScheduleEvent) => {
  const label = buildAppointmentTimeTagPresentation(event).label

  return label && label !== "–" ? label : "Termin"
}

const formatDate = (value: Date, options?: Intl.DateTimeFormatOptions) =>
  value.toLocaleDateString("de-DE", {
    timeZone: "Europe/Berlin",
    ...options,
  })

const formatTime = (value: Date) =>
  value.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Berlin",
  })

const compactDate = (event: ScheduleEvent) => {
  const source =
    (event as { start_at?: string | null }).start_at ??
    (event as { timeframe_start_at?: string | null }).timeframe_start_at

  if (!source) return null

  const date = new Date(source)

  if (Number.isNaN(date.getTime())) return null

  return formatDate(date, { day: "2-digit", month: "2-digit" })
}

const historyDefinition = (
  event: ScheduleEvent,
  labelOverride?: string | null,
): StatusDefinition<string> => {
  const status = normalizeStatus((event as { status?: string | null }).status)
  const label = status ? displayScheduleStatusLabel(status) : "Termin"
  const base = buildScheduleEventCommunicationTagPresentation({
    status: event.status,
    event,
  }).definition

  if (base)
    return {
      ...base,
      value: `${status}-${event.id}`,
      label: labelOverride ?? `${label} ${compactDate(event) ?? ""}`.trim(),
    }

  return {
    value: `${status || "UNKNOWN"}-${event.id}`,
    label: labelOverride ?? label,
    color: "blue",
    icon: mdiCalendarClockOutline,
  }
}

const cleanLabel = (value: unknown) => `${value ?? ""}`.trim() || null
const isUuidLike = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)

const formatOccurredAt = (value?: string | null) => {
  const parsed = parseDateValue(value)

  if (!parsed) return null

  return `${formatDate(parsed, { day: "2-digit", month: "2-digit", year: "numeric" })}, ${formatTime(parsed)} Uhr`
}

const formatCompactOccurredAt = (value?: string | null) => {
  const parsed = parseDateValue(value)

  if (!parsed) return null

  return `um ${formatTime(parsed)} (${formatDate(parsed, { day: "2-digit", month: "2-digit" })})`
}

const findCancellationEntry = (event: ScheduleEvent) =>
  [...(event.lifecycle_entries ?? [])]
    .filter(
      (entry) =>
        normalizeStatus(entry.kind) === "CANCELED" ||
        normalizeStatus(entry.to_status) === "CANCELED",
    )
    .sort((left, right) => parseDate(right.occurred_at) - parseDate(left.occurred_at))[0] ?? null

const sourceCode = (value?: string | null) => {
  const source = cleanLabel(value)?.toUpperCase()

  if (!source) return null

  const aliases: Record<string, string> = {
    APPOINTMENT_PORTAL: "PORTAL",
    PUBLIC_APPOINTMENT_PORTAL: "PORTAL",
    PORTAL: "PORTAL",
    BOT: "WEB_BOT",
    "BOT_APPOINTMENTS.CANCEL_APPOINTMENT": "WEB_BOT",
    MANUAL: "APP",
    "KEHRBLATT-CONTROL": "APP",
    KEHRBLATT_CONTROL: "APP",
    "SCHEDULE_EVENT.RETROACTIVE_CANCEL": "APP",
    "SCHEDULE_EVENT.DELETE": "APP",
    "SCHEDULE_EVENT.BULK_CANCEL": "APP",
    CALENDAR_SYNC: "CALENDAR_PROVIDER",
    "CALENDAR_PROVIDER.RECONCILE": "CALENDAR_PROVIDER",
    "CALENDAR_PROVIDER.RECONCILE_EXTERNAL": "CALENDAR_PROVIDER",
    "CALENDAR_PROVIDER.RECONCILE_CONVERTED_APPOINTMENT": "CALENDAR_PROVIDER",
  }

  return aliases[source] ?? source
}

const sourceCodeFromActorLabel = (value?: string | null) =>
  cleanLabel(value)?.startsWith("Online-Terminportal (") ? "PORTAL" : null

const sourceLabel = (source?: string | null) => {
  const labels: Record<string, string> = {
    PORTAL: "Terminportal",
    WEB_BOT: "Chatbot",
    VOICE_BOT: "Voicebot",
    APP: "App",
    SYSTEM: "KehrBlatt",
    CALENDAR_PROVIDER: "Kalendersynchronisierung",
  }

  return labels[sourceCode(source) ?? ""] ?? null
}

const sourcePhrase = (source?: string | null) => {
  const phrases: Record<string, string> = {
    PORTAL: "im Terminportal",
    WEB_BOT: "im Chatbot",
    VOICE_BOT: "im Voicebot",
    APP: "in der App",
    SYSTEM: "in KehrBlatt",
    CALENDAR_PROVIDER: "durch die Kalendersynchronisierung",
  }

  return phrases[sourceCode(source) ?? ""] ?? null
}

const namedActorLabel = (value?: string | null) => {
  const label = cleanLabel(value)

  if (!label) return null

  const portalMatch = label.match(/^Online-Terminportal \((.+)\)$/)
  const normalized = portalMatch?.[1]?.trim() || label

  if (isUuidLike(normalized)) return null

  if (normalized.includes("_") || normalized.includes(".")) return null

  if (
    [
      "Online-Terminportal",
      "KehrBlatt",
      "Google Kalender",
      "Microsoft Outlook",
      "Kalenderanbieter",
      "Automatik",
      "planning",
    ].includes(normalized)
  )
    return null

  return normalized
}

const cancellationPresentation = (
  event: ScheduleEvent,
  cancellationEntry?: ScheduleEventLifecycleEntry | null,
) => {
  const rawActor =
    cleanLabel(event.lifecycle_summary?.canceled_by_label) ??
    cleanLabel(cancellationEntry?.actor_label)
  const actorType = normalizeStatus(
    event.lifecycle_summary?.canceled_actor_type ?? cancellationEntry?.actor_type,
  )
  const source =
    cleanLabel(event.lifecycle_summary?.canceled_source) ??
    cleanLabel(cancellationEntry?.source) ??
    sourceCodeFromActorLabel(rawActor) ??
    (actorType === "CALENDAR_PROVIDER" ? "CALENDAR_PROVIDER" : null)
  const actor = ["SYSTEM", "CALENDAR_PROVIDER"].includes(actorType)
    ? null
    : namedActorLabel(rawActor)
  const occurredAt = event.lifecycle_summary?.canceled_at ?? cancellationEntry?.occurred_at
  const compactTime = formatCompactOccurredAt(occurredAt)
  const originPhrase = sourcePhrase(source)
  const originLabel = sourceLabel(source)
  const normalizedSource = sourceCode(source)
  const timeSuffix = compactTime ? ` ${compactTime}` : ""

  if (actor) {
    return {
      actor,
      source: originLabel,
      occurredAt,
      text: `Abgesagt durch: ${actor}${originPhrase ? ` ${originPhrase}` : ""}${timeSuffix}`,
    }
  }

  if (normalizedSource === "CALENDAR_PROVIDER") {
    return {
      actor: null,
      source: originLabel,
      occurredAt,
      text: `Automatisch durch die Kalendersynchronisierung abgesagt${timeSuffix}`,
    }
  }

  if (originPhrase) {
    return {
      actor: null,
      source: originLabel,
      occurredAt,
      text: `Abgesagt ${originPhrase}${timeSuffix}`,
    }
  }

  return {
    actor: null,
    source: null,
    occurredAt,
    text: `Abgesagt${timeSuffix}`,
  }
}

const confirmedByLabel = (event: ScheduleEvent) =>
  cleanLabel(event.lifecycle_summary?.confirmed_by_label) ??
  cleanLabel(event.appointment_contact?.confirmed_by_label) ??
  cleanLabel(event.confirmed_by_participant?.display_name) ??
  cleanLabel(event.confirmed_by_participant?.email) ??
  cleanLabel(event.confirmed_by_participant?.phone)

const canceledHistoryItem = (event: ScheduleEvent): WorkOrderAppointmentHistoryItem => {
  const cancellationEntry = findCancellationEntry(event)
  const canceled = cancellationPresentation(event, cancellationEntry)
  const previousStatus = cleanLabel(cancellationEntry?.from_status)
  const previousStatusLabel = previousStatus ? displayScheduleStatusLabel(previousStatus) : null
  const timeTag = buildAppointmentTimeTagPresentation(event)
  const label = timeTag.label && timeTag.label !== "–" ? timeTag.label : "Termin"
  const statusLabel = previousStatusLabel ? `abgesagt (zuvor: ${previousStatusLabel})` : "abgesagt"

  return {
    id: event.id,
    label,
    subLabel: canceled.text,
    status: historyDefinition(event, label),
    tooltip: buildRichTooltip([
      tooltipTitle("Termin-Historie"),
      tooltipField("Status", statusLabel),
      timeTag.timeWindowLabel
        ? tooltipField(
            timeTag.usesCustomerWindow ? "Kunden-Zeitfenster" : "Flex-Zeitfenster",
            timeTag.timeWindowLabel,
          )
        : null,
      tooltipField("Kalendertermin", timeTag.scheduledPeriodLabel),
      timeTag.isFlex || timeTag.usesCustomerWindow
        ? tooltipField("Geplante Dauer", timeTag.durationLabel)
        : null,
      timeTag.tooltipDescription ? tooltipText(timeTag.tooltipDescription) : null,
      tooltipField("Mitarbeiter", event.employee_name),
      tooltipField("Bestätigt durch", confirmedByLabel(event)),
      tooltipField("Abgesagt durch", canceled.actor),
      tooltipField("Ursprung", canceled.source),
      tooltipField("Abgesagt am", formatOccurredAt(canceled.occurredAt)),
    ]),
  }
}

export const buildAppointmentHistoryItem = (
  event: ScheduleEvent,
): WorkOrderAppointmentHistoryItem => {
  if (normalizeStatus(event.status) === "CANCELED") return canceledHistoryItem(event)

  const communication = buildScheduleEventCommunicationTagPresentation({
    status: event.status,
    event,
  })
  const statusLabel = communication.label
  const label = `${statusLabel} ${compactDate(event) ?? ""}`.trim()

  return {
    id: event.id,
    label,
    subLabel: statusLabel,
    status: { ...communication.definition, label: eventLabel(event) },
    tooltip:
      communication.tooltip +
      buildRichTooltip([
        tooltipField("Termin", eventLabel(event)),
        tooltipField("Mitarbeiter", (event as { employee_name?: string | null }).employee_name),
      ]),
  }
}

export const buildAppointmentHistoryItems = ({
  workOrderId,
  events,
  currentEventId,
  limit = 2,
}: {
  workOrderId: string
  events?: ScheduleEvent[] | null
  currentEventId?: string | null
  limit?: number
}): WorkOrderAppointmentHistoryItem[] => {
  const historyEvents = (events ?? [])
    .filter(
      (event) => event?.id && event.work_order_id === workOrderId && event.id !== currentEventId,
    )
    .sort((left, right) => eventCreatedTime(right) - eventCreatedTime(left))
    .slice(0, limit)

  return historyEvents.map(buildAppointmentHistoryItem)
}

export const appointmentHistoryFallbackIcon = mdiHistory
